import React, { useState } from "react";
import { AlertTriangle, Phone, CheckCircle2, ArrowRight, Clock, Mail } from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SITE } from "../data/site";

const STEPS = [
  { n: "01", title: "You send us a referral", body: "Fill in the self-referral form on this page, or ask a professional (GP, key worker, support worker) to refer you." },
  { n: "02", title: "We reply within 5 working days", body: "A team member contacts you using your preferred method to confirm we've received your referral and explain next steps." },
  { n: "03", title: "You join the waiting list", body: "We currently operate a waiting list. We'll tell you the expected wait time and check in with you while you wait." },
  { n: "04", title: "We agree next steps", body: "Once a place is available, we discuss what support fits — peer group, workshop, one-to-one session, or signposting to another service." },
];

const SELF_REFER_STEPS = [
  "Scroll to the self-referral form on this page.",
  "Enter your name and how we can reach you (email, phone or text).",
  "Choose your preferred contact method.",
  "Briefly tell us what's going on — use as many or as few words as feels right.",
  "Tick the consent box and press Send.",
];

const initialSelf = {
  name: "",
  contact: "",
  preferred_contact_method: "email",
  description: "",
  consent: true,
};

const initialPro = {
  organisation: "",
  referrer_name: "",
  referrer_email: "",
  referrer_phone: "",
  client_name: "",
  client_contact: "",
  client_needs: "",
  consent_confirmed: false,
};

export default function GetSupport() {
  useReveal();
  usePageMeta({ title: "Get Support", description: "Self-refer or make a professional referral to Thrive Bridge Mental Wellness CIC." });

  const [self, setSelf] = useState(initialSelf);
  const [pro, setPro] = useState(initialPro);
  const [loadingSelf, setLoadingSelf] = useState(false);
  const [loadingPro, setLoadingPro] = useState(false);

  const submitSelf = async (e) => {
    e.preventDefault();
    setLoadingSelf(true);
    try {
      await api.post("/referrals/self", self);
      toast.success("Thank you for reaching out. We'll be in touch within 5 working days.");
      setSelf(initialSelf);
    } catch (err) {
      toast.error("Something went wrong. Please try again, or email hello@thrivebridge.org.uk.");
    } finally {
      setLoadingSelf(false);
    }
  };

  const submitPro = async (e) => {
    e.preventDefault();
    if (!pro.consent_confirmed) {
      toast.error("Please confirm the client has given consent before submitting.");
      return;
    }
    setLoadingPro(true);
    try {
      await api.post("/referrals/professional", pro);
      toast.success("Referral received. Thank you — we'll be in touch shortly.");
      setPro(initialPro);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingPro(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get support"
        title="Get support from Thrive Bridge"
        lead="You can refer yourself, or a professional supporting you can refer on your behalf. All referrals are free and confidential."
      />

      {/* How to self-refer — plain, scannable */}
      <section id="how-to-self-refer" className="container-page mt-10 md:mt-14">
        <div className="grid gap-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7 fade-up">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">How to self-refer</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl text-[var(--tb-text)]">
              How to self-refer in 5 steps
            </h2>
            <ol className="mt-6 space-y-4">
              {SELF_REFER_STEPS.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-[var(--tb-primary)] text-white text-sm font-semibold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[var(--tb-text-2)] text-lg pt-0.5">{s}</span>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#self"
                data-testid="jump-to-self-form"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-5 py-3 font-semibold"
              >
                Go to self-referral form <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#professional"
                data-testid="jump-to-professional-form"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--tb-primary)] text-[var(--tb-primary)] hover:bg-white px-5 py-3 font-semibold"
              >
                Refer someone else
              </a>
            </div>
          </div>

          <aside className="lg:col-span-5 fade-up">
            <div className="bg-white border border-[var(--tb-border)] rounded-3xl p-6 md:p-7">
              <h3 className="font-serif-display text-xl text-[var(--tb-text)] mb-3">Prefer to call or email?</h3>
              <ul className="space-y-3 text-[var(--tb-text-2)]">
                <li className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Phone className="h-4 w-4" /></span>
                  <a href={`tel:${SITE.phoneRaw}`} className="font-semibold text-[var(--tb-text)] hover:text-[var(--tb-primary)]" data-testid="get-support-phone-link">{SITE.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Mail className="h-4 w-4" /></span>
                  <a href={`mailto:${SITE.email}`} className="font-semibold text-[var(--tb-text)] hover:text-[var(--tb-primary)]" data-testid="get-support-email-link">{SITE.email}</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] shrink-0"><Clock className="h-4 w-4" /></span>
                  <span>{SITE.hours}</span>
                </li>
              </ul>

              <div
                role="note"
                data-testid="waiting-list-notice-getsupport"
                className="mt-5 rounded-2xl bg-[var(--tb-surface)] border border-[var(--tb-border)] p-4 text-sm text-[var(--tb-text-2)]"
              >
                <p>
                  <span className="font-semibold text-[var(--tb-text)]">We operate a waiting list.</span> After you refer,
                  we&rsquo;ll be in touch within 5 working days to explain expected wait times.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Crisis */}
      <section className="container-page mt-10">
        <div
          role="region"
          aria-label="Crisis support banner"
          className="rounded-3xl border-2 border-[var(--tb-crisis)]/30 bg-[var(--tb-crisis)]/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4"
        >
          <span className="grid place-items-center h-12 w-12 rounded-full bg-[var(--tb-crisis)] text-white shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h2 className="font-serif-display text-xl md:text-2xl text-[var(--tb-text)] mb-1">
              If you are in crisis right now
            </h2>
            <p className="text-[var(--tb-text-2)]">
              Please do not use this form. Call <a href="tel:999" className="text-[var(--tb-crisis)] underline font-semibold">999</a> if you&rsquo;re in immediate danger,
              or contact Samaritans on <a href="tel:116123" className="text-[var(--tb-crisis)] underline font-semibold">116 123</a> (free, 24/7).
            </p>
          </div>
          <a
            href="tel:116123"
            data-testid="crisis-call-button"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-crisis)] hover:bg-[var(--tb-crisis-hover)] text-white px-5 py-3 font-semibold"
          >
            <Phone className="h-4 w-4" /> Call Samaritans
          </a>
        </div>
      </section>

      {/* Self referral */}
      <section id="self" className="container-page section-spacing">
        <div className="max-w-3xl mb-8 fade-up">
          <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Self-referral</span>
          <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
            Self-referral form
          </h2>
          <p className="mt-4 text-[var(--tb-text-2)] text-lg text-pretty">
            All information is kept confidential. Required fields are marked with an asterisk.
          </p>
        </div>

        <form
          onSubmit={submitSelf}
          data-testid="self-referral-form"
          className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-6 md:p-10 grid gap-5 md:grid-cols-2"
        >
          <Field label="Your name" required>
            <input
              required
              data-testid="self-name-input"
              value={self.name}
              onChange={(e) => setSelf({ ...self, name: e.target.value })}
              className={inputCls}
              placeholder="First name is enough"
            />
          </Field>
          <Field label="How can we reach you?" hint="Email, phone or another contact" required>
            <input
              required
              data-testid="self-contact-input"
              value={self.contact}
              onChange={(e) => setSelf({ ...self, contact: e.target.value })}
              className={inputCls}
              placeholder="e.g. you@email.com or 07… "
            />
          </Field>
          <Field label="Preferred way to be contacted" required>
            <select
              data-testid="self-preferred-method-select"
              value={self.preferred_contact_method}
              onChange={(e) => setSelf({ ...self, preferred_contact_method: e.target.value })}
              className={inputCls}
            >
              <option value="email">Email</option>
              <option value="phone">Phone call</option>
              <option value="text">Text message</option>
              <option value="any">Any of the above</option>
            </select>
          </Field>
          <Field label="What's going on for you?" hint="Share whatever you feel comfortable with" required className="md:col-span-2">
            <textarea
              required
              rows={5}
              data-testid="self-description-input"
              value={self.description}
              onChange={(e) => setSelf({ ...self, description: e.target.value })}
              className={`${inputCls} resize-y`}
              placeholder="There are no right or wrong answers."
            />
          </Field>

          <div className="md:col-span-2 flex items-start gap-3">
            <input
              id="self-consent"
              type="checkbox"
              checked={self.consent}
              onChange={(e) => setSelf({ ...self, consent: e.target.checked })}
              data-testid="self-consent-checkbox"
              className="mt-1 h-4 w-4 rounded border-[var(--tb-border)]"
              required
            />
            <label htmlFor="self-consent" className="text-sm text-[var(--tb-text-2)]">
              I'm happy for Thrive Bridge to contact me about my enquiry. See our{" "}
              <a href="/privacy" className="text-[var(--tb-primary)] underline">privacy policy</a>.
            </label>
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-[var(--tb-muted)]">We typically reply within 5 working days.</p>
            <button
              type="submit"
              disabled={loadingSelf}
              data-testid="self-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold disabled:opacity-60"
            >
              {loadingSelf ? "Sending…" : "Send my message"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </section>

      {/* What to expect */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing">
          <div className="max-w-2xl mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">What to expect</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)]">
              What happens after you refer
            </h2>
          </div>
          <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <li key={s.n} className="fade-up bg-white border border-[var(--tb-border)] rounded-2xl p-7">
                <span className="font-serif-display text-4xl text-[var(--tb-primary)]">{s.n}</span>
                <h3 className="mt-2 font-serif-display text-xl text-[var(--tb-text)]">{s.title}</h3>
                <p className="mt-2 text-[var(--tb-text-2)]">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Professional referral */}
      <section id="professional" className="container-page section-spacing">
        <div className="max-w-3xl mb-8 fade-up">
          <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Professional referral</span>
          <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
            Refer a client to us
          </h2>
          <p className="mt-4 text-[var(--tb-text-2)] text-lg text-pretty">
            For GPs, housing officers, NHS practitioners, social workers, support workers and partner organisations.
            You must have the client&rsquo;s informed consent before submitting a referral.
          </p>
        </div>

        <form
          onSubmit={submitPro}
          data-testid="professional-referral-form"
          className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-6 md:p-10 grid gap-5 md:grid-cols-2"
        >
          <Field label="Your organisation" required>
            <input required data-testid="pro-organisation-input" value={pro.organisation} onChange={(e) => setPro({ ...pro, organisation: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Your name" required>
            <input required data-testid="pro-referrer-name-input" value={pro.referrer_name} onChange={(e) => setPro({ ...pro, referrer_name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Your email" required>
            <input required type="email" data-testid="pro-referrer-email-input" value={pro.referrer_email} onChange={(e) => setPro({ ...pro, referrer_email: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Your phone (optional)">
            <input data-testid="pro-referrer-phone-input" value={pro.referrer_phone} onChange={(e) => setPro({ ...pro, referrer_phone: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Client name" required>
            <input required data-testid="pro-client-name-input" value={pro.client_name} onChange={(e) => setPro({ ...pro, client_name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Client contact (optional)" hint="Only if the client has agreed to be contacted directly">
            <input data-testid="pro-client-contact-input" value={pro.client_contact} onChange={(e) => setPro({ ...pro, client_contact: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Brief summary of needs" required className="md:col-span-2">
            <textarea required rows={5} data-testid="pro-needs-input" value={pro.client_needs} onChange={(e) => setPro({ ...pro, client_needs: e.target.value })} className={`${inputCls} resize-y`} />
          </Field>

          <div className="md:col-span-2 flex items-start gap-3 rounded-2xl bg-[var(--tb-surface)] p-4">
            <input
              id="pro-consent"
              type="checkbox"
              checked={pro.consent_confirmed}
              onChange={(e) => setPro({ ...pro, consent_confirmed: e.target.checked })}
              data-testid="pro-consent-checkbox"
              className="mt-1 h-4 w-4 rounded border-[var(--tb-border)]"
              required
            />
            <label htmlFor="pro-consent" className="text-sm text-[var(--tb-text-2)]">
              I confirm the client has given informed consent to be referred to Thrive Bridge, and to share the
              information above for the purpose of receiving support.
            </label>
          </div>

          <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="flex items-center gap-2 text-xs text-[var(--tb-muted)]">
              <CheckCircle2 className="h-4 w-4 text-[var(--tb-primary)]" /> GDPR-compliant · stored securely
            </p>
            <button
              type="submit"
              disabled={loadingPro}
              data-testid="pro-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold disabled:opacity-60"
            >
              {loadingPro ? "Submitting…" : "Submit referral"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-[var(--tb-border)] bg-white px-4 py-3 text-[var(--tb-text)] placeholder:text-[var(--tb-muted)] focus:border-[var(--tb-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--tb-primary)]/20 transition-all";

function Field({ label, hint, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-[var(--tb-text)] mb-2">
        {label} {required && <span className="text-[var(--tb-crisis)]" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-[var(--tb-muted)]">{hint}</p>}
    </div>
  );
}
